import axios, { AxiosPromise } from 'axios';
import MyError from './Error';
import { accessToken, accessTokenHeader, getDomainAndPort } from './Utils';
import {
  IUserSecurityEvent,
  SecurityEventLevel,
} from 'store/entity/IUserSecurityEvent';
import { SystemConfigView } from 'store/entity/ISystemConfig';
import { RootState } from 'store/store';
import { IThisUserServer } from 'store/thisUser/thisUserSlice';
import { ITwoFactorAuthenticationParams } from 'store/users/usersType';

const domainAndPort = getDomainAndPort();
const { protocolDomain, port } = domainAndPort;

const baseURL = `${protocolDomain}${port !== '80' ? `:${port}` : ''}/makh/rest`;
const MakhsanApi = axios.create({
  baseURL,
});

export const convertToForm = (data: Record<string, any>) => {
  const formData = new URLSearchParams();
  Object.keys(data).forEach((key) => {
    if (data[key] !== null && data[key] !== undefined)
      formData.append(key, data[key]);
  });
  return formData;
};
MakhsanApi.interceptors.response.use(undefined, (err) => {
  let error;
  if (err.response) {
    error = new MyError(err.response.data, err.response.status);
  } else if (err.request) {
    error = new MyError(err.request, 'request');
  } else {
    error = new MyError(err, 'request');
  }
  return Promise.reject(error);
});

export const secureRegister = (
  email: string,
  password: string,
  captcha: string
) => {
  return MakhsanApi.post(
    '/user/secureRegister',
    convertToForm({
      email,
      'g-recaptcha-response': captcha,
      password,
    }),
    {
      params: {},
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  );
};

// not used in project
export const updateUser = (
  state: RootState,
  deleted: boolean | null,
  firstName: string | null,
  lastName: string | null,
  password: string | null,
  active2fa: boolean | null,
  enable: boolean | null,
  validIps: string | undefined,
  timeout: number | undefined,
  userId: number
) => {
  return MakhsanApi.post(
    '/user/editUser',
    convertToForm({
      ...accessToken(state),
      deleted,
      firstName,
      lastName,
      password,
      user_id: userId,
      validIps,
      timeout,
      active2fa,
      enabled: enable,
    }),
    {
      headers: {
        ...accessTokenHeader(state),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      params: {},
    }
  );
};

// not used in project
export const unblockUser = (state: RootState, userId: number) => {
  return MakhsanApi.post(
    '/user/unblock',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        userId,
      },
    }
  );
};
// not used in project
export const killUserSessions = (state: RootState, userId: number) => {
  return MakhsanApi.post(
    '/user/killAllSession',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        userId,
      },
    }
  );
};

export const searchUser = (
  state: RootState,
  query: string,
  blocked?: boolean,
  from?: number,
  count?: number
) => {
  return MakhsanApi.get('/user/searchUser', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      query,
      from: from || 0,
      count: count || 100,
      blocked,
    },
  });
};
export const getAllRoles = (state: RootState) => {
  return MakhsanApi.get('/role/all', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const mangeUserRole = (
  state: RootState,
  userId: number,
  role: string,
  action: string
) => {
  return MakhsanApi.get('/user/manageRoles', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      user_id: userId,
      roles: role,
      action,
    },
  });
};

export const searchSecurityLogs = (
  state: RootState,
  count?: number,
  type?: string,
  startDate?: number,
  endDate?: number,
  ip?: string,
  level?: SecurityEventLevel,
  targetUserId?: number,
  toId?: number
): AxiosPromise<IUserSecurityEvent[]> => {
  return MakhsanApi.get('/security/viewLog', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      count,
      type,
      startDate,
      endDate,
      ip,
      level,
      targetUserId,
      toId,
    },
  });
};
export const searchRequestLogs = (
  state: RootState,
  endUUID: string | undefined,
  endDate: number | undefined,
  userId: number | undefined,
  app: string | undefined,
  path: string | undefined,
  count: number | undefined
): AxiosPromise<any> => {
  return MakhsanApi.get('/log/view', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      startUuid: endUUID,
      startDate: endDate,
      userId,
      appName: app,
      endpointPath: path,
      count,
      important: true,
    },
  });
};

export const myLoginActivity = (
  state: RootState
): AxiosPromise<IUserSecurityEvent[]> => {
  return MakhsanApi.get('/security/myLoginActivity', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};
export const getAllConfigs = (
  state: RootState
): AxiosPromise<IUserSecurityEvent[]> => {
  return MakhsanApi.get('/security/getAllConfig', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};
export const updateConfig = (
  state: RootState,
  configView: SystemConfigView
): AxiosPromise<SystemConfigView> => {
  return MakhsanApi.post('/security/setConfig', configView, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const selfUserInfo = (state: RootState, userId: number) => {
  return MakhsanApi.get('/user/info', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      user_id: userId,
    },
  });
};

export const enterWithPhone = (
  phone: string,
  dryRun: boolean = false
): AxiosPromise<string> => {
  return MakhsanApi.get('/user/enterWithPhoneV2', {
    params: {
      phone,
      dryRun,
    },
  });
};

export const authenticate2FA = (
  phone: string | null,
  email: string | null,
  password: string | null,
  staySignedIn: boolean = false,
  dryRun: boolean = false
): AxiosPromise<ITwoFactorAuthenticationParams> => {
  return MakhsanApi.post(
    '/user/2FAEntrance',
    convertToForm({
      phone,
      password,
      email,
      dryRun,
      staySignedIn,
    }),
    {
      params: {},
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  );
};

export const enterWithSingleAccessCode = (
  accessCode: string,
  staySignedIn: boolean
): AxiosPromise<IThisUserServer> => {
  return MakhsanApi.post(
    '/user/enterWithSingleAccessCode',
    convertToForm({ access_code: accessCode, staySignedIn }),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  );
};

export const authenticate = (
  email: string,
  password: string,
  staySignedIn: boolean
): AxiosPromise<IThisUserServer> => {
  return MakhsanApi.get('/user/authenticate', {
    params: {
      email,
      password,
      staySignedIn,
    },
  });
};

export const requestChangePhoneNumber = (phone: string, state: RootState) => {
  return MakhsanApi.get('/user/requestChangePhoneNumber', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      phone,
    },
  });
};

export const validatePhoneNumber = (
  state: RootState,
  token: string,
  secret: string
) => {
  return MakhsanApi.get('/user/validateChangePhoneNumber', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      secret,
      token,
    },
  });
};

export const renewToken = (state: RootState) =>
  MakhsanApi.get('/user/renewToken', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });

export const loginWithJWT = (token: string, serviceId: number) => {
  return MakhsanApi.get('/user/enterWithJWT', {
    params: {
      serviceId,
      token: `${token}`,
    },
  });
};

export const loginWithAccessTokenJWT = (accessToken: string) => {
  return MakhsanApi.post(
    '/user/loginWithAccessTokenJWT',
    {
      accessToken,
    },
    {}
  );
};
